import {
  Await,
  useLoaderData,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ActivityTemplate, Intro } from '../../models';
import { Suspense, useEffect } from 'react';
import Spinner from '../../share/Spinner';
import AddChatroomButton from '../chatroom-list/AddChatroomButton';
import { ReactComponent as IntroDivider1 } from '../../assets/intro-divider-1.svg';
import { ReactComponent as IntroDivider2 } from '../../assets/intro-divider-2.svg';
import IntroItem from './IntroItem';
import ViewMaterialButton from './ViewMaterialButton';
import { useLIFFUser } from '../../contexts/LIFFUserContext';
import { CustomerProfile } from '../../models/customerProfile';
import { useTranslation } from 'react-i18next';

export default function TemplateIntroduction() {
  const [t] = useTranslation(['chatroom']);
  const deferredData = useLoaderData() as { data: Intro[] };
  const navigate = useNavigate();
  const { state } = useLocation() as { state: { template: ActivityTemplate } };
  const { isABO } = useLIFFUser() as CustomerProfile;
  const buttonStyleClass = (hasTwoButton: Boolean) =>
    hasTwoButton ? 'w-5/12' : 'w-4/5';

  useEffect(() => {
    if (!state || !state.template) {
      navigate('/chatrooms');
    }
  }, [state, navigate]);

  const template = state.template;
  const onStepBtnClick = (target: Intro) => {
    document.querySelector(`#${target.id}`)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const wrapFunc = (data: Intro[]) => {
    return (
      <div className="flex flex-col flex-shrink-0 w-full">
        <div className="relative overflow-clip h-52 max-h-52">
          <div className="absolute top-2.5 right-2.5 py-1 px-3 bg-white rounded-xl text-xs font-bold text-group-info">
            {t('activity_templates_duration', '{{count}} 天的活動', {
              count: template.duration,
            })}
          </div>
          <img
            alt={template.name}
            src={template.imageUrl}
            className="object-cover object-center h-full w-full"
          />
        </div>

        <div className="relative flex flex-col w-full">
          {isABO && (
            <div className="absolute top-[-20px] flex flex-row w-full justify-evenly">
              <div className={buttonStyleClass(template.materialUrl !== null)}>
                <AddChatroomButton templateId={template.id} />
              </div>

              {template.materialUrl ? (
                <div className={buttonStyleClass(true)}>
                  <ViewMaterialButton
                    templateMaterialUrl={template.materialUrl}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
          )}

          <span
            className={
              'font-bold text-xl text-center' +
              (isABO || template.materialUrl ? ' mt-8' : ' mt-4')
            }
          >
            {template.name}
          </span>
          <p className="p-4">{template.description}</p>
        </div>
        <IntroDivider1 className="w-full h-full" />
        {data.length > 0 ? (
          <>
            <span className="font-semibold text-center my-4">
              {t('activity_templates_intro_title', '互動內容預覽')}
            </span>
            <div className="flex gap-4 overflow-x-scroll overflow-y-hidden h-14 mx-4">
              {data.map((intro, index) => (
                <button
                  key={index}
                  className="h-11 w-11 rounded-full flex justify-center items-center text-intro-text shadow-lg"
                  onClick={() => onStepBtnClick(intro)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </>
        ) : (
          <></>
        )}
        <IntroDivider2 className="w-full h-full" />
        <div className="flex flex-col gap-4 px-4 pb-4 mt-6">
          {data.map((intro, index) => {
            return <IntroItem key={index} data={intro} />;
          })}
        </div>
      </div>
    );
  };

  return (
    <Suspense fallback={<Spinner />}>
      <Await resolve={deferredData.data}>{wrapFunc}</Await>
    </Suspense>
  );
}
